/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react"
import { motion } from "framer-motion"
import { MoreVertical, Edit3, Trash2, AlertTriangle, Play } from "lucide-react"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Lecture } from "@/types/lectures"
import { getYoutubeThumbnail } from "@/utils/getYoutubeThumbnail"

interface LectureCardProps {
  item: Lecture
  index: number
  onSelect: (item: Lecture) => void
  onEdit?: (item: Lecture) => void
  onDelete?: (item: Lecture) => void
  theme: {
    accent: string
    border: string
    bg: string
    glow: string
    fill: string
  }
}

export const LectureCard = ({
  item,
  index,
  onSelect,
  onEdit,
  onDelete,
  theme,
}: LectureCardProps) => {
  const { user } = useAuth()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(item)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <>
      <AlertDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
      >
        <AlertDialogContent className="border-white/10 bg-zinc-900 text-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 text-rose-500">
              <AlertTriangle size={24} />
              <AlertDialogTitle className="text-xl font-black uppercase italic">
                Excluir Palestra?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2 text-zinc-400">
              Você está prestes a remover a palestra: <br />
              <span className="font-bold text-white">"{item.nome}"</span>.
              <br />
              <br />
              Esta ação é permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-zinc-800">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-rose-600 font-bold uppercase tracking-widest text-white hover:bg-rose-700"
            >
              Sim, excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: (index % 4) * 0.05 }}
        onClick={() => onSelect(item)}
        className="group relative flex cursor-pointer flex-col overflow-hidden rounded-[24px] border border-white/10 bg-zinc-900/40 shadow-lg transition-all duration-300 hover:border-primary/50 hover:bg-zinc-900/80 hover:shadow-primary/5"
      >
        {user?.gestaoApp && (
          <div className="absolute right-3 top-3 z-30">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="flex size-9 items-center justify-center rounded-full border border-white/20 bg-zinc-950/80 text-white shadow-xl backdrop-blur-md transition-all hover:bg-primary hover:text-black active:scale-90"
                >
                  <MoreVertical size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 border-white/10 bg-zinc-900 p-2 text-white shadow-2xl"
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit?.(item)
                  }}
                  className="cursor-pointer gap-3 rounded-lg py-2.5 focus:bg-white/5 focus:text-white"
                >
                  <Edit3
                    size={16}
                    className="text-primary"
                  />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Editar</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsDeleteModalOpen(true)
                  }}
                  className="cursor-pointer gap-3 rounded-lg py-2.5 text-red-500 focus:bg-red-500/10 focus:text-red-500"
                >
                  <Trash2 size={16} />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Excluir</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <div className="relative aspect-video overflow-hidden bg-zinc-800">
          <img
            src={getYoutubeThumbnail(item.link)}
            alt={item.nome}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="flex size-14 items-center justify-center rounded-full bg-primary text-black shadow-xl ring-4 ring-primary/20">
              <Play
                fill="currentColor"
                size={24}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <span className={`mb-2 text-[9px] font-black uppercase tracking-[0.2em] ${theme.accent}`}>
            {item.categoria}
          </span>

          <h3 className="mb-6 line-clamp-2 flex-1 text-base font-bold leading-tight text-zinc-100 transition-colors group-hover:text-primary">
            {item.nome}
          </h3>

          <p
            className={`mt-auto border-t border-white/5 pt-4 text-[10px] font-bold uppercase tracking-wide text-zinc-500`}
          >
            {new Date(item.data).toLocaleDateString("pt-BR", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </motion.div>
    </>
  )
}
